import { useEffect, useState } from 'react';
import { ArrowLeft, ExternalLink, Copy, Check, Lock, Package, Database, AlertCircle, RefreshCw, Eye, ChevronDown, ArrowLeftRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import type { Capsule } from '../types';
import type { CollectClient } from '../lib/api';
import { capsuleProtection } from '../lib/protection';
import { capsuleStorage, storedBlobSha256 } from '../lib/capsuleStorage';
import { capsuleErrorMessage } from '../lib/capsuleErrors';
import { CapsuleContents } from './CapsuleContents';
import { CapsuleCompare } from './CapsuleCompare';
import { StatusPill } from './StatusPill';

export function CapsuleDetail({
  client,
  capsule: initial,
  onBack,
}: {
  client: CollectClient;
  capsule: Capsule;
  onBack: () => void;
}) {
  const [capsule, setCapsule] = useState<Capsule>(initial);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [showContents, setShowContents] = useState(false);
  const [pickingCompare, setPickingCompare] = useState(false);
  const [compareCandidates, setCompareCandidates] = useState<Capsule[] | null>(null);
  const [compareWith, setCompareWith] = useState<Capsule | null>(null);
  // View contents answered that this server no longer has the content: the Storage field says so.
  const [contentUnavailable, setContentUnavailable] = useState(false);

  // Lazy-load other capsules of same workspace when picker opens
  useEffect(() => {
    if (!pickingCompare || compareCandidates) return;
    let cancelled = false;
    client.listCapsules(capsule.workspace_id)
      .then((res) => {
        if (cancelled) return;
        // Exclude self, sort newest first (already from backend, but enforce client-side)
        const others = res.capsules
          .filter((c) => c.id !== capsule.id)
          .sort((a, b) => (a.create_date < b.create_date ? 1 : -1));
        setCompareCandidates(others);
      })
      .catch(() => {
        if (!cancelled) setCompareCandidates([]);
      });
    return () => { cancelled = true; };
  }, [pickingCompare, compareCandidates, client, capsule.workspace_id, capsule.id]);

  useEffect(() => {
    // Fetch fresh on mount
    setRefreshing(true);
    client.getCapsule(initial.id)
      .then(setCapsule)
      .catch((e) => setError(capsuleErrorMessage(e)))
      .finally(() => setRefreshing(false));
    setContentUnavailable(false);
  }, [client, initial.id]);

  const copy = (key: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  // Imperio HIVE link (the user's screenshot showed action-2256/<id> works)
  const imperioUrl = `https://qodoonect-production.up.railway.app/odoo/action-vat_services.action_hive_data_capsule/${capsule.id}`;
  const protection = capsuleProtection(capsule.pqc_algorithm);
  // Where the content is recorded as kept, in user words; the store's name and bucket path stay out of the UI.
  const storage = capsuleStorage(capsule.storage_backend, { contentUnavailable });
  const blobSha256 = storedBlobSha256(capsule.storage_ref);
  const ProtectionIcon = protection.kind === 'encrypted' ? Lock : Package;

  // Compare mode takes over the entire detail view
  if (compareWith) {
    return (
      <CapsuleCompare
        client={client}
        capsuleA={capsule}
        capsuleB={compareWith}
        onBack={() => { setCompareWith(null); setPickingCompare(false); }}
      />
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      {/* Back nav */}
      <button onClick={onBack} className="btn btn-ghost mb-6">
        <ArrowLeft size={16} />
        Back to capsules
      </button>

      {/* Hero */}
      <div className="glass-panel-strong rounded-2xl p-8 mb-6">
        <div className="flex items-start gap-5">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center heartbeat" style={{
            background: 'linear-gradient(135deg, #10B981, #064E3B)',
          }}>
            <ProtectionIcon size={26} style={{ color: '#F0FDF4' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-mono text-xl font-semibold truncate">{capsule.name}</h1>
              <StatusPill capsule={capsule} />
              {refreshing && <RefreshCw size={14} className="animate-spin" style={{ color: 'rgba(240,253,244,0.4)' }} />}
            </div>
            <p className="display text-3xl mb-1">Workspace · <span style={{ color: '#10B981' }}>{capsule.workspace_id}</span></p>
            <p className="text-sm" style={{ color: 'rgba(240,253,244,0.5)' }}>
              Created {new Date((capsule.create_date || '').replace(' ', 'T')).toLocaleString()} · v{capsule.version}
            </p>
          </div>
          <div className="flex flex-col gap-2 flex-none relative">
            <button
              onClick={() => setShowContents((v) => !v)}
              className={`btn ${showContents ? 'btn-primary' : 'btn-ghost'}`}
            >
              <Eye size={14} />
              {showContents ? 'Hide contents' : 'View contents'}
            </button>
            <button
              onClick={() => setPickingCompare((v) => !v)}
              className={`btn ${pickingCompare ? 'btn-primary' : 'btn-ghost'}`}
            >
              <ArrowLeftRight size={14} />
              Compare with…
              <ChevronDown size={12} style={{ marginLeft: 2, transition: 'transform 0.2s', transform: pickingCompare ? 'rotate(180deg)' : 'rotate(0)' }} />
            </button>
            <AnimatePresence>
              {pickingCompare && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="absolute right-0 top-full mt-1 w-80 z-30 glass-panel-strong rounded-xl overflow-hidden"
                  style={{ borderColor: '#10B981' }}
                >
                  <div className="px-3 py-2 label" style={{ borderBottom: '1px solid rgba(16,185,129,0.2)', color: '#10B981' }}>
                    Other capsules in <span className="font-mono">{capsule.workspace_id}</span>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {!compareCandidates && (
                      <div className="px-3 py-3 text-xs flex items-center gap-2" style={{ color: 'rgba(240,253,244,0.5)' }}>
                        <RefreshCw size={12} className="animate-spin" /> Loading…
                      </div>
                    )}
                    {compareCandidates && compareCandidates.length === 0 && (
                      <div className="px-3 py-4 text-xs" style={{ color: 'rgba(240,253,244,0.5)' }}>
                        No other capsules in this workspace yet. Send another capsule first.
                      </div>
                    )}
                    {compareCandidates?.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => { setCompareWith(c); setPickingCompare(false); }}
                        className="w-full text-left px-3 py-2 hover:bg-emerald-500/5 border-b border-emerald-500/5 transition-colors"
                      >
                        <div className="font-mono text-xs truncate" style={{ color: '#F0FDF4' }}>{c.name}</div>
                        <div className="text-[10px] mt-0.5" style={{ color: 'rgba(240,253,244,0.45)' }}>
                          {(c.content_size / 1024).toFixed(1)} KB · {(c.create_date || '').slice(0, 16)} · v{c.version}
                        </div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <a href={imperioUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
              <ExternalLink size={14} />
              Open in Imperio
            </a>
          </div>
        </div>
      </div>

      {error && (
        <div className="glass-panel rounded-xl p-4 mb-6 flex items-center gap-3" style={{ borderColor: 'rgba(239,68,68,0.4)' }}>
          <AlertCircle size={18} style={{ color: '#F87171' }} />
          <span style={{ color: '#FCA5A5' }}>{error}</span>
        </div>
      )}

      {/* Detail grid */}
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Capsule ID" value={String(capsule.id)} mono onCopy={(v) => copy('id', v)} copied={copied === 'id'} />
        <Field label="Schema Type" value={capsule.schema_type} mono />
        <Field label="Content Size" value={formatBytes(capsule.content_size)} />
        <Field label="Version" value={`v${capsule.version}`} />
        <Field
          label="Content Hash (SHA-256)"
          value={capsule.content_hash}
          mono
          full
          onCopy={(v) => copy('hash', v)}
          copied={copied === 'hash'}
        />
        <Field label="Protection" value={protection.description} />
        <Field label="Algorithm (declared by sender)" value={capsule.pqc_algorithm} mono />
        <Field label="Storage" value={storage.label} hint={storage.description} warn={storage.contentUnavailable} />
        <Field label="Source Type" value={capsule.source_type || '—'} />
        <Field label="Tags" value={capsule.tags || '—'} mono full />
        {capsule.btc_anchor_txid && (
          <Field
            label="BTC Anchor TX"
            value={String(capsule.btc_anchor_txid)}
            mono full
            onCopy={(v) => copy('btc', v)}
            copied={copied === 'btc'}
          />
        )}
        {blobSha256 && (
          <details className="glass-panel rounded-xl p-4 md:col-span-2">
            <summary className="label cursor-pointer select-none">Technical details</summary>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="label">Stored blob SHA-256</span>
                <button
                  onClick={() => copy('blob', blobSha256)}
                  className="opacity-50 hover:opacity-100 transition-opacity"
                  title="Copy"
                >
                  {copied === 'blob' ? <Check size={14} style={{ color: '#34D399' }} /> : <Copy size={14} />}
                </button>
              </div>
              <div className="font-mono text-sm break-all">{blobSha256}</div>
              <p className="text-xs mt-2" style={{ color: 'rgba(240,253,244,0.5)' }}>
                The SHA-256 of the content exactly as the server stores it: compressed, or for capsules
                encrypted with the CLI, the encrypted envelope.
              </p>
            </div>
          </details>
        )}
      </div>

      {/* Contents (decompressed bundle) — expandable */}
      <AnimatePresence>
        {showContents && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            <div className="mt-6 mb-2 flex items-center gap-3">
              <ChevronDown size={18} style={{ color: '#10B981' }} />
              <h2 className="display text-2xl">Capsule contents</h2>
              <span className="label">decompressed + verified locally</span>
            </div>
            <CapsuleContents
              client={client}
              capsuleId={capsule.id}
              onContentUnavailable={() => setContentUnavailable(true)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action footer */}
      <div className="glass-panel rounded-xl p-6 mt-6">
        <div className="flex items-start gap-4">
          <Database size={20} style={{ color: '#10B981' }} className="flex-none mt-1" />
          <div className="flex-1">
            <h3 className="font-semibold text-base mb-2">What this capsule contains</h3>
            <p className="text-sm leading-relaxed mb-3" style={{ color: 'rgba(240,253,244,0.65)' }}>
              A snapshot of <span className="font-mono" style={{ color: '#34D399' }}>{capsule.workspace_id}</span> at the time it was sent:
              all sources, summaries, concepts, and KG triples bundled, gzipped, and SHA-256 hashed.
              The hash above proves nothing has changed since.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,253,244,0.5)' }}>
              {protection.kind === 'encrypted'
                ? 'It is wrapped in an ML-KEM-768 + AES-256-GCM envelope; decrypt it with the CLI.'
                : 'It is compressed, not encrypted. ML-KEM-768 envelopes come only from the CLI (send-to-hive --encrypt) until browser-side encryption lands.'}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Field({
  label,
  value,
  hint,
  warn,
  mono,
  full,
  onCopy,
  copied,
}: {
  label: string;
  value: string;
  hint?: string;
  /** Show the hint as a warning (e.g. the content could not be read). */
  warn?: boolean;
  mono?: boolean;
  full?: boolean;
  onCopy?: (v: string) => void;
  copied?: boolean;
}) {
  return (
    <div className={`glass-panel rounded-xl p-4 ${full ? 'md:col-span-2' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="label">{label}</span>
        {onCopy && (
          <button
            onClick={() => onCopy(value)}
            className="opacity-50 hover:opacity-100 transition-opacity"
            title="Copy"
          >
            {copied ? <Check size={14} style={{ color: '#34D399' }} /> : <Copy size={14} />}
          </button>
        )}
      </div>
      <div className={`${mono ? 'font-mono text-sm' : 'text-base'} break-all`}>{value}</div>
      {hint && (
        <div className="text-xs mt-1" style={{ color: warn ? '#FCD34D' : 'rgba(240,253,244,0.5)' }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(2)} KB (${n.toLocaleString()} bytes)`;
  return `${(n / 1024 / 1024).toFixed(2)} MB (${n.toLocaleString()} bytes)`;
}
